import { z } from 'zod'
import { apiRequest } from './client'

// ── Schemas ──────────────────────────────────────────────────────────────────
// Defined inline (rather than in shared-types) to keep this feature self
// contained; migrate into the generated client when orval lands.

export const approverOverrideSchema = z.object({
  id: z.string(),
  coveredApproverId: z.string(),
  coverApproverId: z.string(),
  coveredApproverName: z.string(),
  coverApproverName: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  active: z.boolean(),
  createdBy: z.string(),
  revokedAt: z.string().nullable(),
  revokedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type ApproverOverride = z.infer<typeof approverOverrideSchema>

export interface ApproverOverrideCreateRequest {
  coveredApproverId: string
  coverApproverId: string
  startsOn: string
  endsOn: string
}

// FastAPI's OkResponse wraps payloads in `{ok, data}`; accept a bare body too.
// Structural parser type (not `z.ZodType<T>`) so TS infers T from the schema's
// parse() return rather than the ZodType generic — mirrors api/delegations.ts.
function unwrap<T>(raw: unknown, schema: { parse(data: unknown): T }): T {
  const enveloped = z
    .object({ ok: z.literal(true), data: z.unknown() })
    .safeParse(raw)
  if (enveloped.success) return schema.parse(enveloped.data.data)
  return schema.parse(raw)
}

/** GET /api/v1/admin/approver-overrides — HR-only listing. */
export async function listApproverOverrides(options?: {
  activeOnly?: boolean
}): Promise<ApproverOverride[]> {
  const qs = options?.activeOnly ? '?active_only=true' : ''
  const raw = await apiRequest<unknown>(`/api/v1/admin/approver-overrides${qs}`)
  return unwrap(raw, z.array(approverOverrideSchema))
}

/** POST /api/v1/admin/approver-overrides — HR-only create. */
export async function createApproverOverride(
  body: ApproverOverrideCreateRequest,
): Promise<ApproverOverride> {
  const raw = await apiRequest<unknown>('/api/v1/admin/approver-overrides', {
    method: 'POST',
    body,
  })
  return unwrap(raw, approverOverrideSchema)
}

/** POST /api/v1/admin/approver-overrides/{id}/revoke — HR-only revoke. */
export async function revokeApproverOverride(id: string): Promise<ApproverOverride> {
  const raw = await apiRequest<unknown>(
    `/api/v1/admin/approver-overrides/${encodeURIComponent(id)}/revoke`,
    { method: 'POST' },
  )
  return unwrap(raw, approverOverrideSchema)
}
